import { useMemo, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, CircleHelp, ExternalLink, Plus, Trash2, UserRound, X } from 'lucide-react'
import { STATES, pointFromText, suggestHandle } from '../lib/places'
import { newPlaceFaq, visibleFaqs } from '../lib/catalog'
import type { PlaceFaq, PlaceForm as Form, PlaceProfile, Producer } from '../types'
import { AmenityPicker } from './AmenityPicker'
import { ProfileFields } from './ProfileFields'
import { SuggestField } from './SuggestField'

type Props = {
  value: Form
  onChange: (next: Form) => void
  disabled?: boolean
  // Inside an event: the less used parts fold away.
  compact?: boolean
  // A saved place's own profile, shown instead of the choice.
  profile?: PlaceProfile | null
  // The profiles that can become the place's: any without a place.
  producers: Producer[]
  onOpenProfile?: (id: string) => void
  onPendingImage: (pending: boolean) => void
  onError: (message: string) => void
}

const PROFILE_MODES: { value: Form['profile']['mode']; label: string; hint: string }[] = [
  { value: 'create', label: 'Criar perfil', hint: 'A página do lugar no app, com foto, bio e links.' },
  { value: 'link', label: 'Vincular perfil existente', hint: 'Um perfil já cadastrado passa a ser o deste lugar.' },
  { value: 'none', label: 'Sem perfil', hint: 'O lugar aparece só como endereço nos eventos.' },
]

// A place as the app uses it (PRD.md, item 98): name and address, what it
// offers, its questions (item 99) and its profile, which holds the photo, bio
// and links. The same in Lugares and inside an event.
export function PlaceForm({ value, onChange, disabled, compact, profile, producers, onOpenProfile, onPendingImage, onError }: Props) {
  const set = (patch: Partial<Form>) => onChange({ ...value, ...patch })
  const setProfile = (patch: Partial<Form['profile']>) => set({ profile: { ...value.profile, ...patch } })
  const text = (key: 'name' | 'short_name' | 'address' | 'city') => (event: { target: { value: string } }) => {
    const typed = event.target.value
    if (key !== 'name') return set({ [key]: typed || null })
    // The profile's name and @ follow the place's until they are changed.
    const fields = value.profile.fields
    const name = !fields.name || fields.name === value.name ? typed : fields.name
    const handle = !fields.handle || fields.handle === suggestHandle(fields.name) ? suggestHandle(name) || null : fields.handle
    onChange({ ...value, name: typed, profile: { ...value.profile, fields: { ...fields, name, handle } } })
  }
  const point = pointFromText(value.coordinates ?? '')
  const mapsQuery = [value.name, value.address || value.city].filter(Boolean).join(', ')
  const linkable = useMemo(() => producers
    .filter((item) => !item.place_id)
    .map((item) => ({
      id: item.id, label: item.name,
      detail: [item.kind === 'artist' ? 'Artista' : item.kind === 'venue' ? 'Local' : 'Produtor', item.handle && `@${item.handle}`].filter(Boolean).join(' · '),
      words: item.handle ?? '',
    })), [producers])
  const linked = producers.find((item) => item.id === value.profile.link_id)

  const faqs = visibleFaqs(value.faqs)
  const updateFaq = (id: string, patch: Partial<PlaceFaq>) => set({ faqs: value.faqs.map((faq) => faq.id === id ? { ...faq, ...patch } : faq) })
  const moveFaq = (id: string, direction: -1 | 1) => {
    const shown = [...faqs]
    const index = shown.findIndex((faq) => faq.id === id)
    const destination = index + direction
    if (index < 0 || destination < 0 || destination >= shown.length) return
    const [faq] = shown.splice(index, 1)
    shown.splice(destination, 0, faq)
    set({ faqs: [...shown.map((item, order) => ({ ...item, sort_order: order })), ...value.faqs.filter((item) => item.status === 'deleted')] })
  }
  // Out of view at once; a saved one leaves the app on saving.
  const removeFaq = (id: string) => set({ faqs: value.faqs.map((item) => item.id === id ? { ...item, status: 'deleted' } : item) })
  const addFaq = () => set({ faqs: [...value.faqs, newPlaceFaq('', faqs.length)] })

  const amenities = <div className="amenity-stack"><AmenityPicker name={compact ? 'quick-place' : 'place-editor'} value={value.amenities} disabled={disabled} onChange={(next) => set({ amenities: next })} /></div>
  const fold = (summary: string, children: ReactNode) => compact
    ? <details className="quick-create__more"><summary>{summary}</summary>{children}</details>
    : children

  return (
    <div className="place-form">
      <section className={compact ? 'place-form__part' : 'panel form-section'} aria-label="Dados do lugar">
        <div className="form-grid">
          <label className="field field--wide"><span>Nome *</span><input disabled={disabled} value={value.name} onChange={text('name')} placeholder="Ex.: Theatro Municipal do Rio de Janeiro" /></label>
          <label className="field"><span>Nome curto</span><input disabled={disabled} maxLength={40} value={value.short_name ?? ''} onChange={text('short_name')} placeholder="Ex.: Theatro Municipal" /><small>Para o rodapé e os cards quando o nome é longo. Até 40 caracteres.</small></label>
          <div className="field-pair">
            <label className="field"><span>Cidade</span><input disabled={disabled} value={value.city ?? ''} onChange={text('city')} placeholder="Ex.: Rio de Janeiro" /></label>
            <label className="field field--state"><span>Estado</span><select disabled={disabled} value={value.state ?? ''} onChange={(event) => set({ state: event.target.value || null })}><option value="">—</option>{STATES.map(([uf, name]) => <option key={uf} value={uf} title={name}>{uf}</option>)}</select></label>
          </div>
          <label className="field field--wide"><span>Endereço</span><input disabled={disabled} value={value.address ?? ''} onChange={text('address')} placeholder="Ex.: Praça Floriano, s/n - Centro, Rio de Janeiro - RJ" /></label>
          {/* The numbers Google Maps copies, or a Google Maps link, which becomes them. */}
          <label className="field"><span>Coordenadas</span><input disabled={disabled} value={value.coordinates ?? ''} onChange={(event) => {
            const typed = event.target.value
            set({ coordinates: (/^https?:\/\//i.test(typed.trim()) ? pointFromText(typed) : null) ?? (typed || null) })
          }} placeholder="-22.9090, -43.1765" /><small>{point ? `Latitude ${point.split(', ')[0]} · Longitude ${point.split(', ')[1]}` : 'No Google Maps, clique com o botão direito no ponto e copie os números, ou cole o link do lugar.'}</small></label>
          <div className="field"><span>Conferir no mapa</span><a className="button button--secondary" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Abrir no Google Maps</a></div>
        </div>
      </section>

      <section className={compact ? 'place-form__part' : 'panel form-section'} aria-label="O que o lugar oferece">
        {compact ? fold('O que o lugar oferece (opcional)', amenities) : <>
          <div className="form-section__heading"><div><h2>O que o lugar oferece</h2><p>O app mostra só o que estiver como Sim. Na dúvida, deixe Não conferido.</p></div></div>
          {amenities}
        </>}
      </section>

      <section className={compact ? 'place-form__part' : 'panel form-section'} aria-label="Perguntas do lugar">
        <div className="form-section__heading">
          <span className="section-number"><CircleHelp size={15} /></span>
          <div><h2>Perguntas e respostas</h2><p>Aparecem em todos os eventos deste lugar, depois das perguntas de cada evento. Ex.: dia gratuito, guarda-volumes.</p></div>
          <button type="button" className="button button--secondary" disabled={disabled} onClick={addFaq}><Plus size={16} /> Adicionar pergunta</button>
        </div>
        {faqs.length > 0 && <div className="place-faqs">
          {faqs.map((faq, index) => (
            <div className="place-faq" key={faq.id}>
              <div className="place-faq__heading">
                <strong>Pergunta {index + 1}</strong>
                <div className="faq-card__actions">
                  <button type="button" className="icon-button" disabled={disabled || index === 0} onClick={() => moveFaq(faq.id, -1)} aria-label="Mover pergunta do lugar para cima"><ArrowUp size={16} /></button>
                  <button type="button" className="icon-button" disabled={disabled || index === faqs.length - 1} onClick={() => moveFaq(faq.id, 1)} aria-label="Mover pergunta do lugar para baixo"><ArrowDown size={16} /></button>
                  <button type="button" className="text-button text-button--danger" disabled={disabled} onClick={() => removeFaq(faq.id)}><Trash2 size={15} /> Remover</button>
                </div>
              </div>
              <div className="form-grid">
                <label className="field field--wide"><span>Pergunta *</span><input disabled={disabled} value={faq.question} onChange={(event) => updateFaq(faq.id, { question: event.target.value })} placeholder="Ex.: Tem dia gratuito?" /></label>
                <label className="field field--wide"><span>Resposta *</span><textarea disabled={disabled} rows={3} value={faq.answer} onChange={(event) => updateFaq(faq.id, { answer: event.target.value })} placeholder="Escreva uma resposta curta e objetiva para o público." /></label>
              </div>
            </div>
          ))}
        </div>}
      </section>

      <section className={compact ? 'place-form__part' : 'panel form-section'} aria-label="Perfil do lugar">
        <div className="form-section__heading"><span className="section-number"><UserRound size={15} /></span><div><h2>Perfil do lugar</h2><p>A página do lugar no app, com o @, a foto, a bio e os links: seguidores, eventos e o “Com…” dos eventos apontam para ela.</p></div></div>
        {profile ? (
          <div className="place-profile">
            <div><strong>{profile.name}</strong><span>@{profile.handle}</span></div>
            {onOpenProfile && <button type="button" className="button button--secondary" onClick={() => onOpenProfile(profile.id)}>Abrir perfil</button>}
          </div>
        ) : <>
          <div className="choice-group" role="radiogroup" aria-label="Perfil do lugar">
            {PROFILE_MODES.map((mode) => (
              <label className={`choice ${value.profile.mode === mode.value ? 'is-active' : ''}`} key={mode.value}>
                <input type="radio" name={compact ? 'quick-place-profile' : 'place-profile'} disabled={disabled} checked={value.profile.mode === mode.value} onChange={() => setProfile({ mode: mode.value })} />
                <span><strong>{mode.label}</strong><small>{mode.hint}</small></span>
              </label>
            ))}
          </div>
          {value.profile.mode === 'create' && (
            <ProfileFields ofPlace value={value.profile.fields} disabled={disabled} onChange={(patch) => setProfile({ fields: { ...value.profile.fields, ...patch } })} onPendingImage={onPendingImage} onError={onError} />
          )}
          {value.profile.mode === 'link' && (linked ? (
            <div className="place-profile">
              <div><strong>{linked.name}</strong><span>{linked.handle ? `@${linked.handle}` : 'Sem @'}</span></div>
              <button type="button" className="text-button" disabled={disabled} onClick={() => setProfile({ link_id: null })}><X size={15} /> Trocar</button>
            </div>
          ) : (
            <div className="field"><span>Perfil a vincular</span><SuggestField
              label="Perfil a vincular"
              value=""
              placeholder="Digite o nome ou o @"
              disabled={disabled}
              suggestions={linkable}
              onPick={(id) => setProfile({ link_id: id })}
            /><small>Só aparecem os perfis que ainda não são de outro lugar.</small></div>
          ))}
        </>}
      </section>
    </div>
  )
}
